import React from 'react';
import { useIntl } from 'react-intl';

import {
  differenceInDays,
  getDate,
  getMonth,
  getYear,
  endOfDay,
} from 'date-fns';

type FormattedTimestampProps = {
  date: Date;
  todayDate?: Date;
  isShortVersion?: boolean;
  onlyMonthAndYear?: boolean;
  numOfDays?: number;
};

const getDaysAgoMessage = (numOfDays: number, isShortVersion?: boolean) => {
  const intl = useIntl();
  switch (numOfDays) {
    case 0:
      return intl.formatMessage({ id: 'EP_FORMATTEDTIMESTAMP_TODAY' });
    case 1:
      return intl.formatMessage({ id: 'EP_FORMATTEDTIMESTAMP_YESTERDAY' });
    default:
      return intl.formatMessage(
        {
          id: isShortVersion
            ? 'EP_FORMATTEDTIMESTAMP_DAYSAGO_SHORT'
            : 'EP_FORMATTEDTIMESTAMP_DAYSAGO',
        },
        { number: numOfDays }
      );
  }
};

const getDateFormatted = (
  date = new Date(),
  todayDate = new Date(),
  isShortVersion?: boolean
) => {
  const intl = useIntl();
  let formatted = [];
  const day = getDate(date);
  const year = getYear(date);
  const month = intl
    .formatMessage({ id: isShortVersion ? 'MONTH_NAMES_SHORT' : 'MONTH_NAMES' })
    .split(',')[getMonth(date)];

  formatted.push(day + (intl.locale === 'de' ? '.' : ''));
  formatted.push(month + (intl.locale === 'de' && isShortVersion ? '.' : ''));

  if (year !== getYear(todayDate)) {
    formatted.push(isShortVersion ? year % 1000 : year);
  }

  return formatted.join(' ');
};

const getMonthAndYear = (date: Date, isShortVersion?: boolean) => {
  const intl = useIntl();
  const year = isShortVersion ? getYear(date) % 1000 : getYear(date);
  const month = intl
    .formatMessage({ id: isShortVersion ? 'MONTH_NAMES_SHORT' : 'MONTH_NAMES' })
    .split(',')[getMonth(date)];

  return `${month} ${year}`;
};

const FormattedTimestamp = ({
  date,
  todayDate = new Date(),
  isShortVersion,
  onlyMonthAndYear,
}: FormattedTimestampProps) => {
  const numOfDays = differenceInDays(endOfDay(todayDate), endOfDay(date));

  if (onlyMonthAndYear) {
    return <>{getMonthAndYear(date, isShortVersion)}</>;
  }

  return (
    <>
      {numOfDays < 7
        ? getDaysAgoMessage(numOfDays, isShortVersion)
        : getDateFormatted(date, todayDate, isShortVersion)}
    </>
  );
};

export default FormattedTimestamp;
